/**
 * Firebase Authentication Utilities
 *
 * Provides login, logout, and token management for Firebase Auth.
 * Falls back gracefully when Firebase is not configured.
 */

import {
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  ConfirmationResult,
  User,
  UserCredential,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithPhoneNumber,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendEmailVerification as firebaseSendEmailVerification,
  createUserWithEmailAndPassword as firebaseCreateUser,
  deleteUser as firebaseDeleteUser,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type ActionCodeSettings,
} from 'firebase/auth'
import { getFirebaseAuth, isFirebaseConfigured } from './config'

export type FirebaseUser = User

const waitWithTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T | null> => {
  if (timeoutMs <= 0) {
    return promise
  }

  return await new Promise<T | null>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => resolve(null), timeoutMs)
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      },
      (error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      }
    )
  })
}

/**
 * Sign in with email and password
 */
export const loginWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase Auth is not configured')
  }
  return signInWithEmailAndPassword(auth, email, password)
}

/**
 * Sign in with Google provider
 */
export const loginWithGoogle = async (): Promise<UserCredential> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase Auth is not configured')
  }
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  return signInWithPopup(auth, provider)
}

/**
 * Sign in with phone number (starts OTP flow)
 *
 * Returns a ConfirmationResult that can be used to verify the OTP.
 * Requires a RecaptchaVerifier to be set up on the page.
 */
export const loginWithPhone = async (
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier
): Promise<ConfirmationResult> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase Auth is not configured')
  }
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
}

/**
 * Verify OTP code from phone sign-in
 */
export const verifyOTP = async (
  confirmationResult: ConfirmationResult,
  code: string
): Promise<UserCredential> => {
  return confirmationResult.confirm(code)
}

/**
 * Create a RecaptchaVerifier for phone auth
 *
 * @param containerId - The ID of the DOM element to render the reCAPTCHA widget
 * @param size - 'normal' shows visible widget, 'invisible' for invisible reCAPTCHA
 */
export const createRecaptchaVerifier = (
  containerId: string,
  size: 'normal' | 'invisible' = 'invisible'
): RecaptchaVerifier | null => {
  const auth = getFirebaseAuth()
  if (!auth) {
    return null
  }
  return new RecaptchaVerifier(auth, containerId, {
    size,
    callback: () => {
      // reCAPTCHA solved
    },
    'expired-callback': () => {
      // reCAPTCHA expired
    },
  })
}

/**
 * Link Google sign-in to the currently signed-in Firebase account.
 */
export const linkGoogleProvider = async (): Promise<UserCredential> => {
  const auth = getFirebaseAuth()
  if (!auth || !auth.currentUser) {
    throw new Error('No signed-in Firebase user')
  }

  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  return linkWithPopup(auth.currentUser, provider)
}

/**
 * Link phone sign-in to the currently signed-in Firebase account (starts OTP flow).
 */
export const linkPhoneProvider = async (
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier
): Promise<ConfirmationResult> => {
  const auth = getFirebaseAuth()
  if (!auth || !auth.currentUser) {
    throw new Error('No signed-in Firebase user')
  }
  return linkWithPhoneNumber(auth.currentUser, phoneNumber, recaptchaVerifier)
}

/**
 * Get the linked provider IDs for current Firebase user.
 * Common values: password, google.com, phone
 */
export const getLinkedAuthProviders = (): string[] => {
  const auth = getFirebaseAuth()
  const user = auth?.currentUser
  if (!user) {
    return []
  }
  const providers = user.providerData
    .map((provider) => provider.providerId)
    .filter((providerId): providerId is string => Boolean(providerId))
  return Array.from(new Set(providers))
}

/**
 * Get the current user's ID token
 *
 * Firebase automatically refreshes tokens, so this always returns a valid token.
 */
export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  const auth = getFirebaseAuth()
  if (!auth || !auth.currentUser) {
    return null
  }
  return auth.currentUser.getIdToken(forceRefresh)
}

/**
 * Wait for Firebase auth bootstrap to complete on page/app startup.
 *
 * This avoids request races where persisted app state says "authenticated"
 * but `auth.currentUser` is still being restored by Firebase.
 */
export const waitForAuthStateReady = async (timeoutMs = 1500): Promise<void> => {
  const auth = getFirebaseAuth()
  if (!auth || auth.currentUser) {
    return
  }

  const maybeAuthStateReady = (auth as typeof auth & {
    authStateReady?: () => Promise<void>
  }).authStateReady

  if (typeof maybeAuthStateReady === 'function') {
    await waitWithTimeout(maybeAuthStateReady.call(auth), timeoutMs)
    return
  }

  await new Promise<void>((resolve) => {
    let unsubscribe = () => {}
    const timeoutId = window.setTimeout(() => {
      unsubscribe()
      resolve()
    }, timeoutMs)

    unsubscribe = onAuthStateChanged(auth, () => {
      window.clearTimeout(timeoutId)
      unsubscribe()
      resolve()
    })
  })
}

/**
 * Whether this session was opened with a one-time code sent to a phone, the
 * kind money approvals require. null when there is no Firebase session (the
 * server decides those).
 */
export const isPhoneVerifiedSession = async (): Promise<boolean | null> => {
  const user = getFirebaseAuth()?.currentUser
  if (!user) return null
  const result = await user.getIdTokenResult()
  return result.signInProvider === 'phone'
}

/**
 * Whether the signed-in account has a phone linked, without which it cannot
 * open a phone session at all. null when there is no Firebase session.
 */
export const hasLinkedPhone = async (): Promise<boolean | null> => {
  await waitForAuthStateReady()
  const user = getFirebaseAuth()?.currentUser
  if (!user) return null
  return user.providerData.some((provider) => provider.providerId === 'phone')
}

/**
 * Get the current Firebase user
 */
export const getCurrentFirebaseUser = (): User | null => {
  const auth = getFirebaseAuth()
  return auth?.currentUser ?? null
}

/**
 * Subscribe to auth state changes
 *
 * @param callback - Called with the user when auth state changes
 * @returns Unsubscribe function
 */
export const onAuthChange = (
  callback: (user: User | null) => void
): (() => void) => {
  const auth = getFirebaseAuth()
  if (!auth) {
    // Return no-op unsubscribe
    return () => {}
  }
  return onAuthStateChanged(auth, callback)
}

/**
 * Send a password reset email via Firebase Auth
 */
export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase Auth is not configured')
  }
  return firebaseSendPasswordResetEmail(auth, email)
}

const emailVerificationStorageKey = (uid: string): string =>
  `lipaflow-email-verification-sent-at:${uid}`

/**
 * Send an email verification message for the current Firebase user.
 *
 * Throttled client-side to avoid accidental resend loops when auth state
 * observers re-run after sign-in.
 */
export const sendCurrentUserEmailVerification = async (
  options: {
    actionUrl?: string
    throttleMs?: number
  } = {}
): Promise<void> => {
  const auth = getFirebaseAuth()
  const user = auth?.currentUser
  if (!auth || !user) {
    throw new Error('No signed-in Firebase user')
  }
  if (!user.email || user.emailVerified) {
    return
  }

  const throttleMs = options.throttleMs ?? 60_000
  let throttleKey: string | null = null
  if (typeof window !== 'undefined' && throttleMs > 0) {
    throttleKey = emailVerificationStorageKey(user.uid)
    const sentAt = Number(window.localStorage.getItem(throttleKey) || 0)
    if (sentAt && Date.now() - sentAt < throttleMs) {
      return
    }
  }

  const actionCodeSettings: ActionCodeSettings | undefined = options.actionUrl
    ? { url: options.actionUrl, handleCodeInApp: false }
    : undefined

  await firebaseSendEmailVerification(user, actionCodeSettings)
  if (throttleKey) {
    window.localStorage.setItem(throttleKey, String(Date.now()))
  }
}

export const reloadCurrentFirebaseUser = async (): Promise<User | null> => {
  const auth = getFirebaseAuth()
  const user = auth?.currentUser
  if (!user) {
    return null
  }
  await user.reload()
  return auth.currentUser
}

export const currentUserNeedsEmailVerification = (): boolean => {
  const user = getCurrentFirebaseUser()
  return Boolean(user?.email && !user.emailVerified)
}

/**
 * Create a new Firebase user with email and password
 */
export const createUserWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase Auth is not configured')
  }
  return firebaseCreateUser(auth, email, password)
}

/**
 * Delete the currently signed-in Firebase user.
 *
 * Intended for rollback immediately after client-side signup creates a Firebase
 * identity but backend provisioning returns a definite failure. Firebase may
 * reject this with auth/requires-recent-login if the session is stale.
 */
export const deleteCurrentFirebaseUser = async (): Promise<boolean> => {
  const auth = getFirebaseAuth()
  const user = auth?.currentUser
  if (!auth || !user) {
    return false
  }
  await firebaseDeleteUser(user)
  return true
}

/**
 * Update the current user's password.
 * Throws auth/requires-recent-login if the session is too old.
 */
export const updatePassword = async (newPassword: string): Promise<void> => {
  const auth = getFirebaseAuth()
  if (!auth || !auth.currentUser) {
    throw new Error('No signed-in Firebase user')
  }
  return firebaseUpdatePassword(auth.currentUser, newPassword)
}

/**
 * Re-authenticate with email/password (required before sensitive operations
 * like password change when the session is old).
 */
export const reauthenticateWithPassword = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  const auth = getFirebaseAuth()
  if (!auth || !auth.currentUser) {
    throw new Error('No signed-in Firebase user')
  }
  const credential = EmailAuthProvider.credential(email, password)
  return reauthenticateWithCredential(auth.currentUser, credential)
}

/**
 * Sign out the current user
 */
export const firebaseLogout = async (): Promise<void> => {
  const auth = getFirebaseAuth()
  if (!auth) {
    return
  }
  return signOut(auth)
}

/**
 * Check if Firebase Auth is available and configured
 */
export const isFirebaseAuthAvailable = (): boolean => {
  return isFirebaseConfigured() && getFirebaseAuth() !== null
}

export type { RecaptchaVerifier, ConfirmationResult }
